import { isUsingNewComponents } from "../add-element/utils";
import { FRONTEND_FLAGS } from "../core/components/LaunchDarklyProvider";
import { Site } from "../resources/modelTypes";
import { useMediaQueryState } from "@minted/minted-components";
import { useLDClient } from "launchdarkly-react-client-sdk";
import React, { createContext, ReactNode, useContext, useMemo } from "react";

export type StaticSiteData = {
  id: number;
  isDemoSite: boolean;
  isHolidaySite: boolean;
  isOnlineInvitationSite: boolean;
  isUsingNewComponents: boolean;
  isWeddingSite: boolean;
  siteType: string;
};

export const StaticSiteDataContext = createContext<StaticSiteData>({
  id: 0,
  isDemoSite: false,
  isHolidaySite: false,
  isOnlineInvitationSite: false,
  isUsingNewComponents: false,
  isWeddingSite: false,
  siteType: "",
});

export type StaticSiteDataProviderProps = {
  children: ReactNode;
  site: Site;
};

export const StaticSiteDataProvider = ({
  children,
  site,
}: StaticSiteDataProviderProps) => {
  const providerValue = useMemo(
    () => ({
      id: site.id,
      isDemoSite: site.isDemoSite || false,
      isHolidaySite: site.isHoliday,
      isOnlineInvitationSite: site.isOnlineInvitation,
      isUsingNewComponents: isUsingNewComponents(site),
      isWeddingSite: site.isWedding,
      siteType: site.siteType,
    }),
    [site],
  );

  return (
    <StaticSiteDataContext.Provider value={providerValue}>
      {children}
    </StaticSiteDataContext.Provider>
  );
};

/**
 * Custom hook to get the useNewMobileExperience flag.
 * This must be used inside a LaunchDarklyProvider.
 */
export const useNewMobileExperience = (): boolean => {
  const ldClient = useLDClient();
  const { isOnlineInvitationSite } = useContext(StaticSiteDataContext);
  const mediumMediaQueryState = useMediaQueryState({
    mediaQuerySize: "medium",
  });

  if (!isOnlineInvitationSite) {
    return false;
  }

  if (mediumMediaQueryState !== "BELOW") {
    return false;
  }

  return Boolean(
    ldClient?.variation(FRONTEND_FLAGS.OINewMobileExperience, false),
  );
};
